// @flow
import React from 'react';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import {
  usePropsValidation,
  ValidationErrorsMethods,
} from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import { useValidationApiOrigin } from './validation/ValidationApiOriginContext.js';
import Figure from './Figure.js';
import { getPixelWidthFromColumns, RowContext } from './grid.js';
import { isAbortError } from './utilities.js';

const ORIGIN = 'https://jsx-images.mathspace.co';

type Props = {|
  /** Should typically be something like: "/media/upload/2020/Geometry/tri/10right20.svg" */
  path: string,
  /** The description that screenreaders will use to describe the image to a user */
  accessibilityDescription?: string, // TODO can make mandatory later
  // Dimensions are mandatory so that the layout can be computed
  // by the browser before any image data has been received.
  width: number,
  height: number,
  // There are certain cases where we don't want <Figure> to
  // apply its default margins. At time of writing, SolidifyFigureGrid
  // and SolidifyAnnotatedFigure both require this. I'm not really happy
  // with this approach though, so need to rethink it sometime.
  _removeFigureMargin?: boolean,
  ...CompilerInjectedProps,
|};

const Image = (props: Props) => {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    Image,
  );

  // Assert the image exists (ie. produces a 200 response)
  useValidateImageExistence(props);
  // Assertion that a valid image size is used
  useImageSizeValidation(props, Image);
  // Assertion that an image should be the width of it's containing row
  useValidImageWidthValidation(props, Image);

  const { _performValidation } = props;
  // Ad-hoc assertions
  React.useEffect(() => {
    if (failedValidation || !_performValidation) return;
    const { pathname } = new URL('http://fake.com' + props.path);
    const extensionMatch = pathname.match(/\.(svg|SVG)$/);
    if (extensionMatch === null) {
      addErrors([
        {
          message: `You can only specify a path to an svg image.`,
          ...locations.path,
        },
      ]);
      return;
    } else assertValidSvgContents(`${ORIGIN}${path}`, addErrors, locations);
  });

  const imageRef = React.createRef();

  function validateAspectRatio() {
    if (failedValidation || !_performValidation) return;
    if (imageRef.current !== null && imageRef.current.complete) {
      assertNonDistortedAspectRatio(imageRef.current, addErrors, locations);
    }
  }

  // When we are re-previewing a document, the image is typically already loaded.
  // Thus we need to run this check on every render, and we only use the onLoad
  // callback of the img element on the initial render of a given img src.
  React.useEffect(validateAspectRatio);

  if (failedValidation) return null;

  const { path, accessibilityDescription, width, height } = props;

  return (
    <Figure _removeFigureMargin={props._removeFigureMargin}>
      <img
        ref={imageRef}
        src={`${ORIGIN}${path}`}
        alt={accessibilityDescription}
        width={width}
        height={height}
        onLoad={validateAspectRatio}
        // We make it block so that ancestor elements don't end up with
        // whitespace at the bottom due to lineHeight computations triggered
        // by inline descendents.
        style={{ display: 'block', margin: '0 auto' }}
      />
    </Figure>
  );
};
Image.spec = {
  path: pathChecker,
  accessibilityDescription: t.optional(t.string),
  width: t.number,
  height: t.number,
};

// Enforces that paths are strings which provide a versionId /blah?versionId=1234
export function pathChecker(
  props: { [propName: string]: mixed },
  propName: string,
) {
  const value = props[propName];

  const failure = t.string(props, propName);
  if (failure !== null) return failure;
  // Refinement for Flow as it doesn't know t.string enforces a string type
  if (typeof value !== 'string') {
    throw Error('runtime typecheck invariant failure');
  }

  const url = new URL(`http://fake${value}`);
  const versionId = url.searchParams.get('versionId');
  if (versionId === null) {
    return {
      typeDescription: pathChecker.description,
      reason:
        'must contain a versionId. The `Add image` tool generates the correct path for you.',
    };
  }

  return null;
}
pathChecker.description = 'BucketPath';

function assertNonDistortedAspectRatio(img, addErrors, locations) {
  if (img === null) return;
  const { naturalWidth, naturalHeight } = img;
  // NOTE: do NOT use img.width/height as this reports an integer value, not the attribute value.
  const width = Number(img.getAttribute('width') ?? 0);
  const height = Number(img.getAttribute('height') ?? 0);
  const naturalAspectRatio = naturalWidth / naturalHeight;
  const actualAspectRatio = width / height;
  const tolerance = 0.00001;
  if (Math.abs(naturalAspectRatio - actualAspectRatio) > tolerance) {
    console.error('assertNonDistortedAspectRatio', {
      width,
      height,
      naturalWidth,
      naturalHeight,
      naturalAspectRatio,
      actualAspectRatio,
      absoluteDifference: Math.abs(naturalAspectRatio - actualAspectRatio),
    });
    // TODO: *if* the Chrome bug only ever affects the width/height then we could use the JSX declared
    // dimensions rather than using the erroneous DOM API to avoid the issue.
    const zoom = getBrowserZoom();
    addErrors([
      {
        message: `The width + height you have chosen distorts the aspect ratio of the image. The natural dimensions are ${naturalWidth} × ${naturalHeight} whereas the specified dimensions are ${width} × ${height}. Each dimension must be scaled proportionally.${
          zoom !== '100%'
            ? ` 🚨 Your zoom is set to ${zoom}. There is a Chrome bug that misreports the image size when zoom is not 100%. If you think you have set the dimensions correctly, try fixing your zoom to resolve the issue.`
            : ''
        }`,
        ...locations.Image,
      },
    ]);
  }
}

// TODO async assertions are going to wreak havoc with the context-based system
// if we aren't careful. As the the runtime validation system relies upon all the
// validation completing within a given commit cycle.
async function assertValidSvgContents(url, addErrors, locations) {
  const res = await fetch(url);
  const svg = await res.text();
  const div = document.createElement('div');
  div.innerHTML = svg;
  const svgElement = div.querySelector('svg');
  if (svgElement === null) return;

  assertNoEmbeddedBitmapsInSvg(svgElement, addErrors, locations);
  assertAllTextConvertedToOutlines(svgElement, addErrors, locations);
}

function assertNoEmbeddedBitmapsInSvg(svgElement, addErrors, locations) {
  if (svgElement.querySelector('image') !== null) {
    addErrors([
      {
        message:
          'The svg image you are trying to use contains an embedded bitmap. This defeats the purpose of using vector graphics, so is not allowed.',
        ...locations.path,
      },
    ]);
  }
}

function assertAllTextConvertedToOutlines(svgElement, addErrors, locations) {
  // If there are any <text> nodes, then there exists text that has not had
  // the convert to outlines applied.
  if (svgElement.querySelector('text') !== null) {
    addErrors([
      {
        message:
          'The svg image you are trying to use contains fonts that have not been converted to outlines. This violates our image style guide.',
        ...locations.path,
      },
    ]);
  }
}

// TODO implement
// function assertOnlyValidColors(svgElement, addErrors, locations) {
//   // NOTE that there are heaps of ways to specify colors, so being exhaustive is going to be tricky.
//   // Two notable ways
//   // - Using the <style> tag (I have seen exported svgs doing this)
//   // - Using stroke/fill (and probably many other) properties.
//   // Here is how to get access to the cssRules in the first <style> tag
//   // const stylesheetRules = svgElement.querySelector('style').sheet.cssRules;
// }

function getBrowserZoom() {
  return Math.round((window.outerWidth / window.innerWidth) * 100) + '%';
}

export default Image;

export const FIGURE_COLUMNS = [25, 11, 5];
const FIGURE_WIDTHS = FIGURE_COLUMNS.map(getPixelWidthFromColumns);

export function useImageSizeValidation(
  props: { width: number, ...CompilerInjectedProps },
  component: Function,
) {
  const { pixelWidth } = React.useContext(RowContext);
  const { addErrors } = React.useContext(ValidationErrorsMethods);
  React.useEffect(() => {
    if (!props._performValidation || props._sourceLocations === undefined)
      return;
    const sourceLocation =
      props._sourceLocations.width || props._sourceLocations[component.name];
    const validSize = FIGURE_WIDTHS.includes(props.width);
    const validSizes = FIGURE_WIDTHS.filter((width) => width <= pixelWidth);
    if (!validSize)
      addErrors([
        {
          message: `This figure uses an invalid image size: ${
            props.width
          }px. Only one of the following may be used here: ${validSizes
            .map((x) => `${x}px`)
            .join(', ')}`,
          ...sourceLocation,
        },
      ]);
  });
}

export function useValidImageWidthValidation(
  props: { width: number, ...CompilerInjectedProps },
  component: Function,
) {
  const { addErrors } = React.useContext(ValidationErrorsMethods);
  const { pixelWidth } = React.useContext(RowContext);

  React.useEffect(() => {
    if (!props._performValidation || props._sourceLocations === undefined)
      return;
    const width = props.width;
    const sourceLocation =
      props._sourceLocations.width || props._sourceLocations[component.name];
    const sizes = FIGURE_WIDTHS.filter((width) => width <= pixelWidth).map(
      (w) => `${w}px`,
    );

    if (sizes.length === 0) {
      addErrors([
        {
          message: [
            `There is no valid figure size that can fit in the space provided.`,
            `Please place this figure in a larger container`,
          ].join('\n'),
          ...sourceLocation,
        },
      ]);
      return;
    }

    if (width > pixelWidth)
      addErrors([
        {
          message: [
            `The figure you are using is larger than the available space in this context.`,
            `Suggestion: You may use one of the following figure sizes: ${sizes.join(
              ', ',
            )}`,
          ].join('\n'),
          ...sourceLocation,
        },
      ]);
  });
}

function useValidateImageExistence(props: {
  path: string,
  ...CompilerInjectedProps,
}) {
  const { addErrors } = React.useContext(ValidationErrorsMethods);
  const { path, _performValidation, _sourceLocations } = props;
  const apiOrigin = useValidationApiOrigin();

  React.useEffect(() => {
    if (!_performValidation || _sourceLocations === undefined) return;

    const controller = new AbortController();
    const signal = controller.signal;
    // Many systems don't parse URLs correctly, query params in the image URL
    // can be incorrectly parsed unless we encode the image URL. So even though
    // this technically isn't required, we do so to be resilient against bad code.
    const imageUrl = encodeURIComponent(`${ORIGIN}${path}`);
    fetch(`${apiOrigin}/api/image-existence-check?url=${imageUrl}`, { signal })
      .then((res) => {
        if (!res.ok) {
          return Promise.reject(Error('Network response was not ok'));
        }
        return res.json();
      })
      .then((res) => {
        if (res.exists === false) {
          addErrors([
            {
              message: `The image you are trying to use does not exist. Please check the path.`,
              ..._sourceLocations.path,
            },
          ]);
        } else if (res.exists === null) {
          addErrors([
            {
              message: `${res.message} Since we couldn't verify whether the image actually exists or not, we conservatively produce this error`,
              ..._sourceLocations.path,
            },
          ]);
        }
      })
      .catch((err) => {
        // Abort errors are not "exceptions" they are expected when we
        // abort the fetch when doing effect cleanup.
        if (isAbortError(err)) return;

        console.error(err);
        addErrors([
          {
            message: `Network error during existence check. Since we couldn't verify whether the image actually exists or not, we conservatively produce this error`,
            ..._sourceLocations.path,
          },
        ]);
      });
    return () => {
      controller.abort();
    };
  });
}
